// Channel endpoints (admin JWT). Shapes mirror the API exactly:
//   - Channel       → api/src/services/channel-service.ts (Channel)
//   - the routes    → api/src/http/routes/channels.ts (list/create/delete)
//
// RELAY-GATED: the channel routes are mounted ONLY when the control plane has a
// relay link configured (RELAY_URL + token). Without it, GET /admin/channels is
// an un-mounted route and Hono's notFound returns a BARE 404 (no JSON envelope).
// That is NOT a hard error — it's a soft "channels unavailable" state the screen
// renders distinctly (see `isRelayUnavailable`), separate from a real
// SESSION_NOT_FOUND 404 (which carries that code) or a duplicate-name 400.
//
// Pattern: a thin data source over `apiFetch` (vault: RemoteDataSource /
// Asynchronous_Data_Loading — each call returns a Promise<T> the caller awaits),
// with the success body runtime-validated at the boundary (vault Type_Assertion)
// so envelope drift fails loud as a ContractError instead of an undefined deref.
import { z } from "zod";
import { apiFetch, ApiError } from "./client";

export interface Channel {
  id: string;
  orgId: string;
  name: string;
  /** Relay session id — the value an admin passes to `/cogent map <id> <secret>`. */
  relaySessionId: string;
  createdAt: string;
  /** Active Slack channels mapped to this Cogent channel (CP-side, authoritative). */
  slackChannels: number;
  /** Live agents on this channel, or null when unknown (relay per-channel telemetry
   *  not yet exposed) — render as "unknown", never a fabricated number. */
  liveAgents: number | null;
  /** When true, the channel is never idle-swept by the relay (durable). */
  neverDelete: boolean;
  /** Idle-window override in ms before deletion; null = inherit the relay default
   *  (1 calendar month). Epic Channel-Retention. */
  retentionTimeoutMs: number | null;
}

const channelSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  relaySessionId: z.string(),
  createdAt: z.string(),
  slackChannels: z.number(),
  liveAgents: z.number().nullable(),
  neverDelete: z.boolean(),
  retentionTimeoutMs: z.number().nullable(),
}) satisfies z.ZodType<Channel>;

const channelsResponseSchema = z.object({ channels: z.array(channelSchema) });

export interface CreateChannelInput {
  name: string;
  /** Channel password the relay bcrypt-hashes; the agent uses it to join. */
  secret: string;
}

export async function listChannels(signal?: AbortSignal): Promise<Channel[]> {
  const res = await apiFetch("/admin/channels", { auth: true, signal, schema: channelsResponseSchema });
  return res.channels;
}

export function createChannel(body: CreateChannelInput): Promise<Channel> {
  return apiFetch("/admin/channels", { method: "POST", auth: true, body, schema: channelSchema });
}

export function deleteChannel(id: string): Promise<void> {
  return apiFetch(`/admin/channels/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/**
 * Rotate a channel's join secret (Phase 3). The control plane never stores the
 * secret — this re-keys the relay session only. Agents must rejoin with the new
 * secret. Returns no body (204); the caller keeps the typed secret to show the
 * one-time `/cogent map` command.
 */
export function rotateChannelSecret(id: string, secret: string): Promise<void> {
  return apiFetch(`/admin/channels/${encodeURIComponent(id)}`, {
    method: "PATCH",
    auth: true,
    body: { secret },
  });
}

/** A channel's retention policy (Epic Channel-Retention). Both optional: omit
 *  `timeoutMs` to inherit the relay default (1 calendar month); `neverDelete` makes
 *  the channel permanent (never idle-swept). */
export interface RetentionInput {
  neverDelete?: boolean;
  timeoutMs?: number;
}

/**
 * Set/replace a channel's retention policy (org-admin). Persists `never_delete` +
 * timeout override on the channel row and pushes the policy to the relay (the
 * enforcement point). Returns no body (204).
 */
export function setChannelRetention(id: string, body: RetentionInput): Promise<void> {
  return apiFetch(`/admin/channels/${encodeURIComponent(id)}/retention`, {
    method: "PATCH",
    auth: true,
    body,
  });
}

/**
 * True when the feature itself is unavailable because the control plane has no
 * relay link — the routes aren't mounted, so the app returns a bare 404 with no
 * error envelope (code `HTTP_404`). Deliberately distinct from a SESSION_NOT_FOUND
 * 404 (a real channel-lookup miss, which carries that code) so the screen can show
 * "not enabled yet" rather than a scary failure.
 */
export function isRelayUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === "HTTP_404";
}
